import Keycloak from 'keycloak-js';
import { AxiosRequestConfig } from 'axios';

let keycloakInstance: Keycloak | null = null;
let loginRedirectStarted = false;
let refreshInFlight: Promise<string | undefined> | null = null;

const TOKEN_MIN_VALIDITY_SECONDS = 70;
const TOKEN_FORCE_REFRESH_SECONDS = 24 * 60 * 60;
const TOKEN_KEEPALIVE_MS = 30_000;

export const setKeycloakInstance = (kc: Keycloak) => {
  keycloakInstance = kc;
};

export const getKeycloakInstance = () => keycloakInstance;

export const getToken = (): string | undefined => {
  return keycloakInstance?.token;
};

export const isAuthenticated = (): boolean => {
  return keycloakInstance?.authenticated ?? false;
};

export async function ensureValidToken(
  minValiditySeconds: number = TOKEN_MIN_VALIDITY_SECONDS
): Promise<string | undefined> {
  const kc = keycloakInstance;
  if (!kc?.authenticated) {
    return kc?.token;
  }

  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    try {
      await kc.updateToken(minValiditySeconds);
      return kc.token;
    } catch (error) {
      console.error('Failed to refresh token', error);
      redirectToKeycloakLogin();
      return undefined;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export function startTokenKeepAlive(onToken?: (token: string | undefined) => void): () => void {
  const kc = keycloakInstance;
  if (!kc) {
    return () => {};
  }

  const tick = () => {
    void ensureValidToken().then((token) => onToken?.(token));
  };

  kc.onTokenExpired = tick;

  const onVisible = () => {
    if (document.visibilityState === 'visible') {
      tick();
    }
  };

  document.addEventListener('visibilitychange', onVisible);
  const timer = window.setInterval(tick, TOKEN_KEEPALIVE_MS);

  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    window.clearInterval(timer);
    if (kc.onTokenExpired === tick) {
      kc.onTokenExpired = undefined;
    }
  };
}

export const redirectToKeycloakLogin = (redirectUri?: string) => {
  if (!keycloakInstance || loginRedirectStarted) {
    return;
  }

  loginRedirectStarted = true;
  void keycloakInstance.login(redirectUri ? { redirectUri } : undefined);
};

export async function applyKeycloakToken(
  config: AxiosRequestConfig
): Promise<AxiosRequestConfig> {
  const token = await ensureValidToken();
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}

export async function retryWithRefreshedToken<T>(
  error: { config?: AxiosRequestConfig & { _retry?: boolean }; response?: { status?: number } },
  retry: (config: AxiosRequestConfig) => Promise<T>
): Promise<T> {
  const original = error.config;
  if (!original || original._retry || error.response?.status !== 401) {
    return Promise.reject(error);
  }

  original._retry = true;
  const token = await ensureValidToken(TOKEN_FORCE_REFRESH_SECONDS);
  if (!token) {
    return Promise.reject(error);
  }

  original.headers = original.headers ?? {};
  original.headers.Authorization = `Bearer ${token}`;
  return retry(original);
}
